import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ThankYouPage from "./ThankYou";
import { journeyActions } from "../../store/journeySlice";
import { fetchPolicyStatus } from "../../api/policyActions";
import { generateOtpAction } from "../../api/otpActions";
import {
  saveJourneyCompletion,
  updateJourneyStage,
  redirectPIVCLink,
  saveProposalSummary,
  getJourneyData,
  JOURNEY_STAGE,
  PAYMENT_TYPE_BIZ_CODE,
} from "../../api/journeyService";
import { resolvePreferredOtpIdentifier, verifyOtp } from "../../api/otpService";
import { uploadDocumentAction } from "../../api/documentActions";
import NeedhelpModal from "../../common/components/NeedhelpModal";
import { usePaymentReturnResume } from "../../common/hooks/usePaymentReturnResume";
import BrandLoader from "../../common/components/BrandLoader";
import { useJourney } from "../../common/hooks/useJourney";
import {
  getLeadMobile,
  getLeadEmail,
  getLeadDateOfBirth,
  getLeadDisplayName,
  getLeadGender,
  getLeadNationality,
  LEAD_FIELD_EMAIL,
  LEAD_FIELD_MOBILE,
  mergeCumulativeStagesOrdered,
  isAnnuitantSelfRelationshipValue,
} from "../../common/utils/journeyLeadFields";
import {
  mergeProposerStagesFromJourney,
  mergeAnnuitantStagesFromJourney,
} from "../../common/utils/proposalCumulativeHelpers";
import {
  isNischitJointLifeSelected,
  resolveSecondaryAnnuitantQuoteFields,
} from "../../common/utils/nischitRoleFields";
import { useProposalLeadHydration } from "../../common/hooks/useProposalLeadHydration";
import {
  normalizeJourneyStages,
  mergeDocumentsUploadedFromParsedStages,
  collectDocumentsUploadedDeep,
} from "../../common/utils/journeyResume";
import {
  pickDocumentPreviewFields,
  serializeDocumentFileMetaWithImagePreview,
  stripDocumentSnapshotForApi,
} from "../../common/utils/documentFileMeta";
import DocumentFilePreview from "../../common/components/DocumentFilePreview";
import {
  resolveSlotLaPayload,
  FALLBACK_PR_BY_SLOT,
} from "../../config/documentLaCodes";
import {
  MAX_DOCUMENT_UPLOAD_MB,
  getDocumentUploadOverSizeError,
} from "../../common/utils/documentUploadLimits";
import { logError, logWarning } from "../../common/utils/logger";
import { getSafeRedirectUrl } from "../../common/utils/safeNavigation";
import { TEXT_CONTENT } from "../../constants";
import { getCurrentJourneyContext } from "../../common/utils/journeyContextRuntime";
import { getNischitProductConfig, resolveNischitProductCode } from "../quote/productConfig";

const context = getCurrentJourneyContext();
const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" fill="#10b981" />
    <path
      d="M8 12l3 3 5-5"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path
      d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 11v6M14 11v6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

function mergeCumulativeStages(journey) {
  const raw =
    journey?.stages ??
    journey?.cummulativeJourneyData ??
    journey?.c_cummulativeJourneyData;
  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (parsed && typeof parsed === "object") {
    return mergeCumulativeStagesOrdered(parsed);
  }
  return {};
}

function collectProposalDocumentsSnapshots(root) {
  const out = [];
  const visited = new Set();

  const walk = (node) => {
    if (!node || typeof node !== "object") return;
    if (visited.has(node)) return;
    visited.add(node);

    if (
      Object.prototype.hasOwnProperty.call(node, "proposalDocumentsSnapshot")
    ) {
      out.push(node.proposalDocumentsSnapshot);
    }

    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }

    Object.values(node).forEach(walk);
  };

  walk(root);
  return out;
}

/**
 * Prefer Redux session (leadFormData). Uses quoteResult (e.g. leadInput.emailId) when leadFormData is missing email.
 * Only call getJourneyData when contact is still missing.
 */
async function resolveLeadContact(
  applicationNumber,
  leadData,
  dispatch,
  quoteResult = null,
) {
  let mobile = getLeadMobile(leadData) || getLeadMobile(quoteResult);
  let email = getLeadEmail(leadData) || getLeadEmail(quoteResult);
  if (mobile && email) return { mobile, email };
  if (!applicationNumber) return { mobile, email };
  try {
    const journey = await getJourneyData(applicationNumber);
    const merged = mergeCumulativeStages(journey);
    mobile =
      mobile ||
      getLeadMobile(merged) ||
      getLeadMobile(journey) ||
      getLeadMobile(quoteResult);
    email =
      email ||
      getLeadEmail(merged) ||
      getLeadEmail(journey) ||
      getLeadEmail(quoteResult);
    const next = { ...leadData };
    let changed = false;
    if (!getLeadMobile(leadData) && mobile) {
      next[LEAD_FIELD_MOBILE] = mobile;
      changed = true;
    }
    if (!getLeadEmail(leadData) && email) {
      next[LEAD_FIELD_EMAIL] = email;
      changed = true;
    }
    if (changed) dispatch(journeyActions.setLeadFormData(next));
  } catch (e) {
    logError("[ProposalReview] getJourneyData for lead contact", e);
  }
  return { mobile, email };
}

function nomineeUsesLifeAssuredAddress(nom) {
  const v = nom?.nomineeAddressSameAsProposer;
  return !(
    v === false ||
    v === "false" ||
    String(v).toUpperCase() === "N" ||
    String(v).toUpperCase() === "NO"
  );
}

function isSameBankDetailsAsProposer(value) {
  if (typeof value === "boolean") return value;
  const normalized = String(value ?? "").trim().toLowerCase();
  return (
    normalized === "yes" ||
    normalized === "true" ||
    normalized === "y" ||
    normalized === "1"
  );
}

function resolveSameBankAsProposer(annuitantData, mergedAnnuitantData, prefix) {
  const field =
    prefix === "pa" ? "samePaBankAsProposer" : "sameSaBankAsProposer";
  const nestedKey = prefix === "pa" ? "primaryAnnuitant" : "secondaryAnnuitant";
  return (
    mergedAnnuitantData?.[field] ??
    annuitantData?.[field] ??
    annuitantData?.[nestedKey]?.[field]
  );
}

/** Mirrors resolveSameBankAsProposer for the address toggle — when PA/SA
 * picked "same as proposer", the annuitant form never collects its own
 * addressLine1/city/state/pinCode, so extractAnnuitant's result genuinely
 * has none of those keys. */
function resolveSameAddressAsProposer(annuitantData, mergedAnnuitantData, prefix) {
  const field =
    prefix === "pa"
      ? "paCommunicationAddressSameAsProposerAddress"
      : "saCommunicationAddressSameAsProposerAddress";
  const nestedKey = prefix === "pa" ? "primaryAnnuitant" : "secondaryAnnuitant";
  return (
    mergedAnnuitantData?.[field] ??
    annuitantData?.[field] ??
    annuitantData?.[nestedKey]?.[field]
  );
}

/** When address is same as LA, nominee rows often omit mailing fields — use life assured. */
function formatNomineeAddressForReview(nom, life) {
  if (nomineeUsesLifeAssuredAddress(nom)) {
    const parts = [
      life?.addressLine1,
      life?.addressLine2,
      life?.addressLine3,
      life?.city,
      life?.state,
      life?.pinCode,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "Same as Life Assured";
  }
  const line1 = nom.mailingAddressLine1 || nom.communicationAddressLine1 || "";
  const line2 = nom.mailingAddressLine2 || nom.communicationAddressLine2 || "";
  const line3 = nom.mailingAddressLine3 || nom.communicationAddressLine3 || "";
  const city = nom.city || nom.communicationCity || "";
  const state = nom.state || nom.communicationState || "";
  const pin = nom.pinCode || nom.communicationPinCode || "";
  const parts = [line1, line2, line3, city, state, pin].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "-";
}

const stripTrailingPercent = (value) => {
  let s = String(value).trim();
  let end = s.length - 1;
  while (end >= 0 && s[end] === " ") end -= 1;
  if (end >= 0 && s[end] === "%") {
    end -= 1;
    while (end >= 0 && s[end] === " ") end -= 1;
    s = s.slice(0, end + 1);
  }
  return s.trim();
};

function getNomineeClaimPercentage(nom) {
  const candidates = [
    nom?.allocation,
    nom?.claimPercentage,
    nom?.entitlement,
    nom?.share,
    nom?.nomineeShare,
    nom?.claimShare,
    nom?.NomineeEntitlement,
    nom?.nomineeAllocation,
  ];
  for (const raw of candidates) {
    if (raw == null || raw === "") continue;
    const s = stripTrailingPercent(raw);
    if (s === "") continue;
    const n = Number(s);
    if (Number.isFinite(n)) return String(n);
    return s;
  }
  return null;
}

function getNomineesListFromNomineeStep(nomineeData) {
  if (!nomineeData || typeof nomineeData !== "object") return [];
  if (Array.isArray(nomineeData.nominees) && nomineeData.nominees.length > 0) {
    return nomineeData.nominees;
  }
  const nd = nomineeData.nomineeDetails;
  if (Array.isArray(nd)) return nd;
  if (nd && typeof nd === "object" && Array.isArray(nd.nominees)) {
    return nd.nominees;
  }
  return Array.isArray(nomineeData.nominees) ? nomineeData.nominees : [];
}

const ProposalReview = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { handlePayment: onPayment } = useJourney();
  const [activeTab, setActiveTab] = useState("proposer");
  const [showDocError, setShowDocError] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [loadingOtp, setLoadingOtp] = useState(false);
  const [completingJourney, setCompletingJourney] = useState(false);
  const [loading, setLoading] = useState(false);
  const [documentsHydrated, setDocumentsHydrated] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [pivcStatus, setPivcStatus] = useState(null);
  const [pivcApplicationNumber, setPivcApplicationNumber] = useState("");
  const [pivcError, setPivcError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [documentUploadError, setDocumentUploadError] = useState("");
  /** Inline validation per document slot (visible next to the row). */
  const [documentSlotErrors, setDocumentSlotErrors] = useState({});
  const documentUploadErrorRef = useRef(null);
  const stepData = useSelector((state) => state.journey.stepData || {});
  const leadData = useSelector((state) => state.journey.leadFormData || {});
  const annuitantRelationship = leadData?.annuitantRelationship;
  const quoteResult = useSelector((state) => state.journey?.quoteResult);
  const customizePlanData = useSelector(
    (state) => state.journey?.customizePlanData,
  );
  const customerName = leadData?.fullName;
  const annuitantData = useSelector(
    (state) => state.journey.stepData?.annuitant || {},
  );
  const secondaryAnnuitantDob =
    resolveSecondaryAnnuitantQuoteFields(quoteResult, customizePlanData)
      ?.secondaryAnnuitantDob;

  const documents = useSelector((state) => state.journey.documents || {});
  const documentsRef = useRef(documents);
  documentsRef.current = documents;
  const reduxCaseId = useSelector((state) => state.journey?.caseId);

  const quoteData = useSelector((state) => state.journey?.quoteResult);
   const PLAN_OPTION_MAP = {
  "Single Life Annuity": "single",
  "Single Life Annuity with Return of Premium plus CI/TPD benefit": "single ci",
  "Single Life Annuity with Certain Period of 10 years": "single 10",
  "Single Life Annuity with Certain Period of 15 years": "single 15",
  "Joint Life Annuity": "joint",
  "Joint Life Annuity with Return of Premium plus CI/TPD Benefit": "joint return",
  "Single Life Annuity with Return of Balance of Premium": "single return",
};

// const selectedPlan = useSelector(
//   (state) =>
//     state.journey?.customizePlanData?.selectedPlan
// );

 const {
    leadResult
  } = useJourney();

const selectedPlan =
    leadResult?.annuitantType || quoteData?.biPayload?.selectedPlan || "";

const planOption = PLAN_OPTION_MAP[selectedPlan] || selectedPlan;

  const annuityPayoutAmount = useSelector(
    (state) => state.journey?.quoteResult?.premiumResult?.annuityPerFrequency,
  );
  const annuityPayoutPeriod = useSelector(
    (state) => state.journey?.customizePlanData?.withdrawalAfter,
  );
  const annuityPayoutFrequency = useSelector(
    (state) =>
      state.journey?.quoteResult?.annuityPaymentFrequency ||
      state.journey?.customizePlanData?.annuityFrequency,
  );
  const paymentStatus = quoteData?.summaryData?.paymentFlowMode;
  const totalPayableAmount = quoteData?.quoteSummaryData?.totalPayableAmount;
  const caseId = reduxCaseId || quoteData?.caseId;

  const biApiProductId = useSelector((state) => state.journey?.biApiProductId);

  const reduxData = useSelector((state) => state.journey);
  const journeyData = useSelector(
    (state) => state.journey?.journeyData || null,
  );
  const customerAadhaar = useSelector(
    (state) => state.journey.customerVerificationData?.aadhaar,
  );

  /** Last channel used for OTP generate (mobile preferred) — must match verify. */
  const otpIdentifierRef = useRef(null);

  const { isResuming, isReady } = usePaymentReturnResume();

  const isResumingOrNotReady = isResuming || !isReady;

  const shouldShowThankYouFromJourney = useMemo(() => {
    const currentStage = String(
      journeyData?.currentStage || journeyData?.c_currentStage || "",
    ).toLowerCase();

    const stage10FromStages =
      journeyData?.stages?.["Stage 10.0 Proposal"] ||
      journeyData?.currentStageData?.["Stage 10.0 Proposal"] ||
      null;

    const proposalFormStatus = String(
      stage10FromStages?.proposalFormStatus || "",
    ).toLowerCase();
    const reverseFeedStatus = String(
      stage10FromStages?.reverseFeedStatus || "",
    ).toLowerCase();
    const otpVerified = String(
      stage10FromStages?.IsOTPVerified_YN || "",
    ).toLowerCase();

    if (
      currentStage.includes("thankyoupagereversefeed") ||
      currentStage.includes("thankyou")
    ) {
      return true;
    }

    return (
      proposalFormStatus === "completed" &&
      (reverseFeedStatus.includes("thankyou") || otpVerified === "y")
    );
  }, [journeyData]);

  useEffect(() => {
    if (shouldShowThankYouFromJourney) {
      setShowThankYou(true);
    }
  }, [shouldShowThankYouFromJourney]);

  function extractAnnuitant(data, type = "pa") {
    if (!data || typeof data !== "object") return {};

    const prefix = type === "pa" ? "pa" : "sa";

    const result = {};

    Object.keys(data).forEach((key) => {
      if (key.startsWith(prefix)) {
        const newKey = key.replace(prefix, ""); // remove pa/sa
        const formattedKey = newKey.charAt(0).toLowerCase() + newKey.slice(1);

        result[formattedKey] = data[key];
      }
    });

    return result;
  }

  const life = {
    ...mergeProposerStagesFromJourney(journeyData),
    ...(stepData?.lifeAssured || {}),
    ...(stepData?.proposer || {}),
  };
  const apiCache = useSelector((state) => state.journey.apiCache || {});
  const nomineeData = stepData?.nominee || {};
  const nominees = getNomineesListFromNomineeStep(nomineeData);
  const _sumAssured = useSelector(
    (state) => state.journey?.quoteResult?.coverAmount,
  );
  const _education = useSelector(
    (state) => state.journey?.leadFormData?.educationalLevel,
  );
  const mergedStageData = {
    ...mergeProposerStagesFromJourney(journeyData),
    ...life,
  };
  const mergedAnnuitantData = {
    ...mergeAnnuitantStagesFromJourney(journeyData),
    ...(annuitantData || {}),
  };
  const primaryAnnuitant = extractAnnuitant(mergedAnnuitantData, "pa");
  const secondaryAnnuitant = extractAnnuitant(mergedAnnuitantData, "sa");
  const paUsesProposerBank = isSameBankDetailsAsProposer(
    resolveSameBankAsProposer(annuitantData, mergedAnnuitantData, "pa"),
  );
  const saUsesProposerBank = isSameBankDetailsAsProposer(
    resolveSameBankAsProposer(annuitantData, mergedAnnuitantData, "sa"),
  );
  const paUsesProposerAddress = isSameBankDetailsAsProposer(
    resolveSameAddressAsProposer(annuitantData, mergedAnnuitantData, "pa"),
  );
  const saUsesProposerAddress = isSameBankDetailsAsProposer(
    resolveSameAddressAsProposer(annuitantData, mergedAnnuitantData, "sa"),
  );
  const shouldHideBankAccountProof = (tab) => {
    // Proposer always uploads their own bank account proof — there's no
    // "same as proposer" concept for the proposer themselves.
    if (tab === "primary") return paUsesProposerBank;
    if (tab === "secondary") return saUsesProposerBank;
    return false;
  };

  function getAnnuitantDetails(data) {
    return [
      { label: "Father Name", value: data.fatherName },
      { label: "Mother Name", value: data.motherName },
      {
        label: "Nationality",
        value: data.citizenship
          ? data.citizenship
          : mergedStageData.citizenship,
      },
      { label: "Marital Status", value: data.maritalStatusLabel  || "-"},
      { label: "Job Description", value: data.jobDescription },
    ].filter((item) => item.value);
  }

  // "No" here means NRI / foreign nationality; getLeadNationality normalizes
  // every persisted form (Yes/No/NRI/true/false/option refs) to "Yes"/"No".
  const isNriNationality = getLeadNationality(leadData) === "No";

  const DOC_LABELS = useMemo(() => {
    const base = {
      photograph: "Photograph",
      addressProof: "Address Proof",
      ageProof: "Age Proof",
      identificationProof: "Identification Proof",
      incomeProof: "Financial/Income Documents",
      bankAccountProof: "Bank Account Proof",
    };
    if (isNriNationality && activeTab === "proposer") {
      base.fatcaDeclaration = "FATCA/CRS Declaration Form";
    }
    return base;
  }, [isNriNationality, activeTab]);

  const REQUIRED_DOCS = useMemo(() => {
    const base = [
      "photograph",
      "addressProof",
      "ageProof",
      "identificationProof",
      "incomeProof",
      "bankAccountProof",
    ];

  if (isNriNationality && activeTab === "proposer") {
    return [...base, "fatcaDeclaration"];
  }
    return base;
  }, [isNriNationality, activeTab]);

  const maskPhone = (phone) => {
    if (!phone) return "-";
    return phone.slice(0, 2) + "XXXXX" + phone.slice(-3);
  };

  const maskAccount = (acc) => {
    if (!acc) return "XXXX XXXX XXXX";
    return `XXXX XXXX ${acc.slice(-4)}`;
  };

  /** Aadhaar last-4 display, e.g. "xxxx - xxxx - 1234" */
  const formatAadhaarMaskedDisplay = (raw) => {
    const digits = String(raw ?? "").replace(/\D/g, "");
    if (digits.length < 4) return "";
    const last4 = digits.slice(-4);
    return `xxxx - xxxx - ${last4}`;
  };

   const activeProductCode = useMemo(
      () => resolveNischitProductCode(leadData || {}),
      [leadData],
    );

    const nischitProductDisplayName = useMemo(() => {
    const name = getNischitProductConfig(activeProductCode)?.name?.trim();
    if (!name) {
    return "INL Nishchit Pension";
  }
  return name;
  }, [activeProductCode]);

  const s = useMemo(
    () => formatAadhaarMaskedDisplay(customerAadhaar?.number),
    [customerAadhaar?.number],
  );

  const calculateAge = (dob) => {
    if (!dob) return 0;

    const today = new Date();
    const birthDate = new Date(dob);
    if (Number.isNaN(birthDate.getTime())) return "-";

    let age = today.getFullYear() - birthDate.getFullYear();

    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

  const formatDOB = (dob) => {
    if (!dob) return "/";
    const [year, month, day] = dob.split("-");
    return `${day}-${month}-${year}`;
  };

  const resolveOwnershipDobForUpload = ({
    leadData,
    life,
    annuitantData,
    activeTab,
  }) => {
    const proposerDob =
      leadData?.dateOfBirth ||
      life?.dateOfBirth ||
      leadData?.lifeAssuredDob ||
      "";
    if (activeTab === "primary") {
      return annuitantData?.paDob || proposerDob;
    }
    if (activeTab === "secondary") {
      return annuitantData?.saDob || proposerDob;
    }
    return proposerDob;
  };

  const maskMobile = (mobile) => {
    if (!mobile) return "";
    return `+91 *** **${mobile.slice(-4)}`;
  };

  const handleUploadClick = (key) => {
    document.querySelector(`input[name="${key}"]`)?.click();
  };

  const applicationNumber =
    useSelector((state) => state.journey?.applicationNumber) ||
    leadData?.applicationNumber;

  const persistProposalDocumentsSnapshot = useCallback(
    async (nextDocuments) => {
      if (!applicationNumber || !caseId) return;
      try {
        await updateJourneyStage(
          applicationNumber,
          caseId,
          JOURNEY_STAGE.PROPOSAL_SUMMARY_WITH_PVC,
          {
            proposalDocumentsSnapshot: stripDocumentSnapshotForApi(
              nextDocuments || {},
            ),
          },
        );
      } catch (error) {
        // Best-effort sync only: local Redux state already updated.
        logError(
          "[ProposalReview] Failed to persist proposal document snapshot",
          error,
        );
      }
    },
    [applicationNumber, caseId],
  );

  const handleRemoveDocument = (key, event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    const next = {
      ...documents,
      [activeTab]: {
        ...(documents?.[activeTab] || {}),
      },
    };
    delete next[activeTab][key];
    if (key === "addressProof") {
      delete next[activeTab].addressProofPermanent;
      delete next[activeTab].addressProofCommunication;
    }
    if (key === "identificationProof") {
      delete next[activeTab].idProof;
    }
    dispatch(journeyActions.setDocuments(next));
    void persistProposalDocumentsSnapshot(next);
    setDocumentUploadError("");
    setDocumentSlotErrors((prev) => {
      const n = { ...prev };
      delete n[key];
      return n;
    });
    const input = document.querySelector(`input[name="${key}"]`);
    if (input) input.value = "";
  };

  const handleFileUpload = async (key, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const label = DOC_LABELS[key] || key;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const validExtensions = ["jpg", "jpeg", "png", "pdf"];
    if (!validExtensions.includes(ext)) {
      const msg = `${label}: Invalid format. Please upload jpg, png, or pdf.`;
      setDocumentSlotErrors((prev) => ({ ...prev, [key]: msg }));
      setDocumentUploadError(msg);
      e.target.value = "";
      return;
    }
    const sizeErr = getDocumentUploadOverSizeError(file, label);
    if (sizeErr) {
      setDocumentSlotErrors((prev) => ({ ...prev, [key]: sizeErr }));
      setDocumentUploadError(sizeErr);
      e.target.value = "";
      return;
    }
    setDocumentUploadError("");
    setDocumentSlotErrors((prev) => {
      const n = { ...prev };
      delete n[key];
      return n;
    });
    setLoading(true);
    try {
      const formData = new FormData();
      if (!applicationNumber) {
        throw new Error(
          "Application number is missing. Please restart the journey.",
        );
      }
      formData.append("applicationNumber", applicationNumber);
      const ownershipDob = resolveOwnershipDobForUpload({
        leadData,
        life,
        annuitantData,
        activeTab,
      });
      if (ownershipDob) {
        formData.append("dateOfBirth", ownershipDob);
      }
      formData.append("documentCategory", "KYC");
      let la = resolveSlotLaPayload(key, {
        formData: activeTab === "proposer" ? life : annuitantData,
        apiCache,
        personType: activeTab,
      });
      if (!la) {
        la = FALLBACK_PR_BY_SLOT[key] || {};
      }
      if (!la.documentTypeCode || !la.documentType) {
        throw new Error(
          "Could not resolve document type. Check your document selections on the proposal form.",
        );
      }
      formData.append("documentTypeCode", la.documentTypeCode);
      formData.append("documentType", la.documentType);
      formData.append("docFormat", file.name.split(".").pop().toUpperCase());
      formData.append("docPageSideFB", "M");
      formData.append("addedBy", "system");
      formData.append("sourceFrom", "online");
      formData.append("file", file);
      const response = await dispatch(uploadDocumentAction(formData));
      if (response?.success) {
        const slotMeta = await serializeDocumentFileMetaWithImagePreview(
          file,
          response,
        );
        dispatch(
          journeyActions.setDocuments({
            ...documents,
            [activeTab]: {
              ...(documents?.[activeTab] || {}),
              [key]: slotMeta,
            },
          }),
        );

        void persistProposalDocumentsSnapshot({
          ...documents,
          [activeTab]: {
            ...(documents?.[activeTab] || {}),
            [key]: slotMeta,
          },
        });
        setDocumentUploadError("");
        setDocumentSlotErrors((prev) => {
          const n = { ...prev };
          delete n[key];
          return n;
        });
        setDocumentsHydrated(true);
      } else {
        throw new Error(response?.errorMessage || "Upload failed");
      }
    } catch (error) {
      logError("Upload failed", error);
      setDocumentUploadError(error?.message || "Document upload failed");
    } finally {
      setLoading(false);
    }
  };

  const normalizeJourneyDocSlot = useCallback((val) => {
    if (val == null) return null;
    if (typeof val === "object" && val.__fileMeta) {
      return {
        ...val,
        ...pickDocumentPreviewFields(val),
        name: String(val.name || val.fileName || "document"),
        __fileMeta: true,
      };
    }
    if (
      typeof val === "object" &&
      (val.uploaded === true ||
        val.uploaded === "true" ||
        String(val.uploaded).toLowerCase() === "y")
    ) {
      const fileName =
        val.fileName || val.filename || val.file || val.name || "document";
      const out = {
        __fileMeta: true,
        name: String(fileName),
        fromJourneyDocumentsUploaded: true,
      };
      Object.assign(out, pickDocumentPreviewFields(val));
      return out;
    }
    if (val && typeof val === "object" && !Array.isArray(val)) {
      const fileName =
        val.fileName || val.filename || val.file || val.name || "";
      const previews = pickDocumentPreviewFields(val);
      if (fileName || Object.keys(previews).length > 0) {
        return {
          __fileMeta: true,
          name: String(fileName || "document"),
          fromJourneyDocumentsUploaded: true,
          ...previews,
        };
      }
    }
    return val;
  }, []);

  // Proposer's bank document is uploaded inline on the Bank Details step
  // (ProposerTab's own upload dropdown, storing a raw `file` field + a
  // `bankAccountProof` type label directly on the bank stage) — a
  // completely separate path from the shared per-slot `documents` map every
  // other document type (and PA/SA's own bank proof) goes through via
  // UploadDocumentsModal. Without this fallback, `documents.proposer
  // .bankAccountProof` is always empty even after a real upload, so the
  // review page showed it as missing.
  const getProposerBankProofFallback = () =>
    mergedStageData?.file ? mergedStageData.file : null;

  const getDocForRequiredKey = (key) => {
    const source = documents?.[activeTab] || {};
    let raw = null;
    if (key === "addressProof") {
      raw =
        source?.addressProof ||
        source?.addressProofPermanent ||
        source?.addressProofCommunication ||
        null;
    } else if (key === "identificationProof") {
      raw = source?.identificationProof || source?.idProof || null;
    } else if (key === "bankAccountProof" && activeTab === "proposer") {
      raw = source?.bankAccountProof || null;
    } else {
      raw = source?.[key] || null;
    }
    return normalizeJourneyDocSlot(raw);
  };

  const getDocForTab = (tab, key) => {
    const source = documents?.[tab] || {};
    let raw = null;

    if (key === "addressProof") {
      raw =
        source?.addressProof ||
        source?.addressProofPermanent ||
        source?.addressProofCommunication ||
        null;
    } else if (key === "identificationProof") {
      raw = source?.identificationProof || source?.idProof || null;
    } else if (key === "bankAccountProof" && tab === "proposer") {
      raw = source?.bankAccountProof || getProposerBankProofFallback();
    } else {
      raw = source?.[key] || null;
    }
    return normalizeJourneyDocSlot(raw);
  };

  const TABS = useMemo(() => {
    const journeyType = context?.journeyType;

    let tabs = [{ id: "proposer", label: "Proposer Details" }];

    if (journeyType === "nischit-pension") {
      // Handles literal "self", "Self" (as resumed from the backend), and
      // the Nischit lead form's encoded option ref — a bare `=== "self"`
      // check only matched the live in-session lowercase value and left
      // the wrong tabs showing after a resume.
      const isSelf = isAnnuitantSelfRelationshipValue(annuitantRelationship);
      const isJoint = isNischitJointLifeSelected(quoteResult, customizePlanData);

      // Case: Self + Joint → ONLY Secondary
      if (isSelf && isJoint) {
        return [
          { id: "proposer", label: "Proposer Details" },
          { id: "secondary", label: "Secondary Annuitant" },
        ];
      }

      // Case: Not Self + Joint → BOTH
      if (!isSelf && isJoint) {
        return [
          { id: "proposer", label: "Proposer Details" },
          { id: "primary", label: "Primary Annuitant" },
          { id: "secondary", label: "Secondary Annuitant" },
        ];
      }

      // Case: Self + Single → ONLY proposer
      if (isSelf && !isJoint) {
        return tabs;
      }

      // Case: Not Self + Single → ONLY Primary
      return [
        { id: "proposer", label: "Proposer Details" },
        { id: "primary", label: "Primary Annuitant" },
      ];
    }

    return tabs;
  }, [context?.journeyType, annuitantRelationship, quoteResult, customizePlanData]);

  const missingDocs = useMemo(() => {
    const tabs = TABS.map((t) => t.id);

    const missing = [];

    tabs.forEach((tab) => {
      REQUIRED_DOCS.forEach((doc) => {
        if (doc === "fatcaDeclaration" && tab !== "proposer") {
          return;
        }
        if (doc === "bankAccountProof" && shouldHideBankAccountProof(tab)) {
          return;
        }
        const uploaded = getDocForTab(tab, doc);
        if (!uploaded) {
          missing.push({ tab, doc });
        }
      });
    });

    return missing;
  }, [documents, paUsesProposerBank, saUsesProposerBank, TABS, REQUIRED_DOCS, activeTab]);

  const persistRehydrated = useSelector((s) => s._persist?.rehydrated);
  useProposalLeadHydration(applicationNumber, persistRehydrated);

  useEffect(() => {
    if (!documentUploadError) return;
    const el = documentUploadErrorRef.current;
    if (el && typeof el.scrollIntoView === "function") {
      try {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      } catch {
        /* ignore */
      }
    }
  }, [documentUploadError]);

  useEffect(() => {
    setDocumentsHydrated(false);
  }, [applicationNumber]);

  useEffect(() => {
    if (documentsHydrated) return;
    if (!applicationNumber) return;
    // Wait for redux-persist so we merge journey URLs on top of rehydrated `documents`,
    // and avoid racing the initial empty store before localStorage loads.
    if (persistRehydrated !== true) return;

    let cancelled = false;
    (async () => {
      try {
        const journey = await getJourneyData(applicationNumber);
        if (cancelled || !journey) return;

        const merged = {};
        const assignSnapshot = (snap) => {
          let parsed = snap;
          if (typeof parsed === "string") {
            try {
              parsed = JSON.parse(parsed);
            } catch {
              parsed = null;
            }
          }
          if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
            Object.assign(merged, parsed);
          }
        };

        collectProposalDocumentsSnapshots(journey).forEach(assignSnapshot);
        assignSnapshot(
          journey.proposalDocumentsSnapshot ??
            journey.c_proposalDocumentsSnapshot,
        );

        const parsedStages = normalizeJourneyStages(journey);
        Object.assign(
          merged,
          mergeDocumentsUploadedFromParsedStages(parsedStages),
        );
        Object.assign(merged, collectDocumentsUploadedDeep(journey));

        const base = { ...documentsRef.current };
        const hasRemote = Object.keys(merged).length > 0;

        if (hasRemote) {
          for (const [k, v] of Object.entries(merged)) {
            const incoming = normalizeJourneyDocSlot(v);
            if (!incoming) continue;
            const cur = normalizeJourneyDocSlot(base[k]);
            if (!cur) {
              base[k] = incoming;
              continue;
            }
            base[k] = {
              ...cur,
              ...incoming,
              __fileMeta: true,
              name: cur.name || incoming.name,
              previewUrl: cur.previewUrl || incoming.previewUrl,
              previewDataUrl: cur.previewDataUrl || incoming.previewDataUrl,
            };
          }
        }

        for (const k of Object.keys(base)) {
          const normalized = normalizeJourneyDocSlot(base[k]);
          if (normalized) base[k] = normalized;
        }

        if (hasRemote || Object.keys(base).length > 0) {
          dispatch(journeyActions.setDocuments(base));
        }
      } catch (e) {
        logError("[ProposalReview] Failed to hydrate documents", e);
      } finally {
        if (!cancelled) setDocumentsHydrated(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    documentsHydrated,
    applicationNumber,
    dispatch,
    normalizeJourneyDocSlot,
    persistRehydrated,
  ]);

  const quoteResultForDisplay = quoteData || {};
  const displayName = useMemo(
    () => getLeadDisplayName(leadData, life, quoteResultForDisplay),
    [leadData, life, quoteResultForDisplay],
  );
  const displayEmail = useMemo(
    () => getLeadEmail(leadData) || getLeadEmail(quoteResultForDisplay),
    [leadData, quoteResultForDisplay],
  );
  const displayMobile = useMemo(
    () => getLeadMobile(leadData) || getLeadMobile(quoteResultForDisplay),
    [leadData, quoteResultForDisplay],
  );
  const displayGender = useMemo(
    () => getLeadGender(leadData, life, quoteResultForDisplay),
    [leadData, life, quoteResultForDisplay],
  );
  const displayDob = useMemo(
    () => getLeadDateOfBirth(leadData, life, quoteResultForDisplay),
    [leadData, life, quoteResultForDisplay],
  );

  const handlePreIssuanceVerification = async () => {
    if (missingDocs.length > 0) {
      const firstMissingTab = missingDocs[0].tab;
      setActiveTab(firstMissingTab);
      setShowDocError(true);
      setPivcError(
        "Please upload all mandatory documents before starting Pre-Issuance Verification.",
      );
      return;
    }

    setShowDocError(false);

    const quoteResultForContact = reduxData?.quoteResult || {};
    const { mobile: leadMobile, email: leadEmail } = await resolveLeadContact(
      applicationNumber,
      leadData,
      dispatch,
      quoteResultForContact,
    );

    // Backend requires `LA_EmailId`.
    const finalLAEmailId = leadEmail;

    if (!finalLAEmailId) {
      setPivcError(
        "Email address is required for PIVC. Please check Life Assured details.",
      );
      return;
    }

    const extractNumber = (val) => {
      if (val == null) return null;
      const s = String(val).trim();
      if (!s) return null;
      // Handle formatted numbers like "6,000,000" or "₹ 41,800"
      const normalized = s.replace(/,/g, "");
      const m = normalized.match(/-?\d+(\.\d+)?/);
      if (!m) return null;
      const n = Number(m[0]);
      return Number.isFinite(n) ? n : null;
    };

    const formatDateYYYYMMDD = (val) => {
      if (!val) return "";
      const s = String(val).trim();
      if (!s) return "";
      // Handle ISO strings: YYYY-MM-DDTHH:mm...
      const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
      return m ? m[1] : s;
    };

    const cleanPivcUrl = (u) => {
      if (!u || typeof u !== "string") return "";
      let s = u.trim();
      if (
        (s.startsWith('"') && s.endsWith('"')) ||
        (s.startsWith("'") && s.endsWith("'"))
      ) {
        s = s.slice(1, -1);
      }
      // Some responses end with encoded quotes (%22 / %27)
      s = s.replace(/%22$/g, "").replace(/%27$/g, "").trim();
      return s;
    };

    const extractAppNumberFromPivcUrl = (u) => {
      if (!u) return "";
      try {
        const parsed = new URL(u);
        const possibleKeys = [
          "Application_Number",
          "applicationNumber",
          "applicationNo",
          "appNo",
          "proposalNo",
        ];
        for (const key of possibleKeys) {
          const val = parsed.searchParams.get(key);
          if (val && String(val).trim()) {
            return String(val).trim();
          }
        }
      } catch (e) {
        // Ignore malformed URL; fallback path handles this.
      }
      return "";
    };

    const quoteResult = quoteResultForContact;
    const quoteSummaryData = quoteResult?.quoteSummaryData || {};
    const premiumResult = reduxData?.premiumResult || {};
    const leadResult = reduxData?.leadResult || {};

    const laName = life?.fullName || leadData?.fullName || leadData?.name || "";
    const laDob = formatDateYYYYMMDD(
      life?.dateOfBirth ||
        leadData?.dateOfBirth ||
        life?.lifeAssuredDob ||
        leadData?.lifeAssuredDob,
    );
    const laMobile =
      leadMobile ||
      life?.mobileNumber ||
      leadData?.mobileNumber ||
      leadData?.proposerMobileNumber ||
      "";

    const laEmail = finalLAEmailId;

    const laAddressLine1 =
      life?.addressLine1 ||
      leadData?.addressLine1 ||
      leadData?.communicationAddressLine1 ||
      "";
    const laAddressLine2 =
      life?.addressLine2 ||
      leadData?.addressLine2 ||
      leadData?.communicationAddressLine2 ||
      "";
    const laAddressLine3 =
      life?.addressLine3 ||
      leadData?.addressLine3 ||
      leadData?.communicationAddressLine3 ||
      "";
    const laCity =
      life?.city || leadData?.city || leadData?.communicationCity || "";
    const laState =
      life?.state || leadData?.state || leadData?.communicationState || "";
    const laPinCode =
      life?.pinCode ||
      leadData?.pinCode ||
      leadData?.communicationPinCode ||
      leadData?.pincode ||
      "";
    // PIVC expects lead-stage occupation; proposal life fields must not override it.
    const laOccupation = String(
      leadData?.proposerOccupation ||
      leadData?.occupation ||
        leadResult?.occupation ||
        leadResult?.leadInput?.occupation ||
        quoteResultForContact?.leadInput?.occupation ||
        quoteResultForContact?.occupation ||
        life?.occupation ||
        life?.jobDescription ||
        "",
    ).trim();

    const coverAmountRaw =
      quoteSummaryData?.coverAmount ??
      quoteResult?.coverAmount ??
      quoteResult?.baseSumAssured ??
      leadData?.coverAmount ??
      leadData?.baseSumAssured ??
      "";
    const sumAssured = extractNumber(coverAmountRaw);

    const policyTermRaw =
      quoteSummaryData?.policyTerm ??
      quoteResult?.policyTerm ??
      leadData?.policyTerm ??
      "";
    const policyTerm = extractNumber(policyTermRaw);

      const maturityDate = new Date();
    maturityDate.setFullYear(maturityDate.getFullYear() + policyTerm);

    const formattedMaturityDate = `${String(
      maturityDate.getMonth() + 1,
    ).padStart(2, "0")}/${maturityDate.getFullYear()}`;

    const paymentTermTenureRaw =
      quoteResult?.paymentTermTenure ??
      quoteSummaryData?.paymentTermTenure ??
      leadData?.paymentTermTenure ??
      leadData?.premiumPayingTerm ??
      quoteResult?.premiumPayingTerm ??
      leadData?.selectedLimitedPayTerm ??
      quoteResult?.biResponse?.[0]?.PPT ??
      quoteResult?.biResult?.InputValidationStatus?.[0]?.PPT;
      "";
  
    const premiumPayingTermNum = extractNumber(paymentTermTenureRaw);

    const premiumFrequencyRaw =
      quoteSummaryData?.premiumFrequency ??
      quoteResult?.premiumFrequency ??
      quoteResult?.premiumPaymentFrequency ??
      leadData?.premiumFrequency ??
      leadData?.premiumPaymentFrequency ??
      "";

    const biModalPremium =
      quoteResult?.biResponse?.[0]?.ModalPremium ??
      quoteResult?.biResult?.InputValidationStatus?.[0]?.ModalPremium;

    const premiumFrequency = String(premiumFrequencyRaw || "");
    const installmentPremiumRaw =
      biModalPremium ??
      quoteSummaryData?.totalPayableAmount ??
      quoteResult?.totalPayableAmount ??
      quoteResult?.premiumAmount ??
      premiumResult?.finalDiscountedPremium ??
      premiumResult?.totalPayableAmount ??
      premiumResult?.finalPremium ??
      null;

    const installmentPremium = extractNumber(installmentPremiumRaw);

    const totalInstPremium = installmentPremium;

    const dynamicPlanName =
      quoteResult?.productName ||
      quoteResult?.biPayload?.productName ||
      quoteSummaryData?.productName ||
      quoteSummaryData?.biPayload?.productName ||
      nischitProductDisplayName ||
      "";

    const defermentPeriodRaw =
      quoteResult?.biPayload?.defermentPeriod ??
      quoteResult?.defermentPeriod ??
      quoteSummaryData?.defermentPeriod ??
      quoteSummaryData?.biPayload?.defermentPeriod ??
      reduxData?.customizePlanData?.withdrawalAfter ??
      reduxData?.customizePlanData?.defermentPeriod ??
      reduxData?.journey?.customizePlanData?.withdrawalAfter ??
      reduxData?.journey?.customizePlanData?.defermentPeriod ??
      leadData?.defermentPeriod ??
      null;

    const deferredPeriod =
      defermentPeriodRaw != null && String(defermentPeriodRaw).trim() !== ""
        ? String(defermentPeriodRaw).trim()
        : null;

    const payload = {
      Added_By: "",
      Application_Number: applicationNumber || "",
      combo_Policy_Term: null,
      Combo_sumassured:null,
      Plan_Option: planOption,
      PIVC_Flag: "Selfie",
      Flow: "1",
      LA_Name: laName || "",
      LA_DOB: laDob || "",
      LA_Mobileno: laMobile || "",
      LA_EmailId: laEmail || "",
      LA_AddressLine1: laAddressLine1 || "",
      LA_AddressLine2: laAddressLine2 || "",
      LA_AddressLine3: laAddressLine3 || "",
      LA_City: laCity || "",
      LA_State: laState || "",
      LA_Pincode: laPinCode || "",
      LA_Occupation: laOccupation || "",
      Login_Mobile: laMobile || "",
      Login_Name: laName || "",
      Source_From: "SE",
      Source: "TAB/Offline",
      Channel: null,
      User_type: "DM-BM",
      Login_SAPCode: "70738327",
      PlanName: dynamicPlanName || nischitProductDisplayName,
      QsLS_30_Medication_Drug_YN: "N",
      QsLS_32_MedicalAilgment_YN: "N",
      QsLS_33_SurgeryPlanned_YN: "N",
      Life_Assured_Mobileno: laMobile || "",
      Annuity_Payout_Amount: annuityPayoutAmount || "",
      Annuity_Payout_Period: annuityPayoutPeriod || "",
      Annuity_Payout_Frequency: annuityPayoutFrequency || "",
      Sumassured: sumAssured ?? "",
      PolicyTerm: policyTerm ?? "",
      PremiumFrequency: premiumFrequency || "",
      PremiumPayingTerm:
        premiumPayingTermNum != null ? String(premiumPayingTermNum) : "",
      Installmentpremium:
        totalInstPremium != null ? String(totalInstPremium) : null,
      Add_Param3: "0",
      Total_InstPremium_ST:
        totalInstPremium != null ? String(totalInstPremium) : null,
      plancode: "180",
      PlanType: dynamicPlanName || nischitProductDisplayName,
      Combo: null,
      Combo_PlanName: null,
      Combo_PremiumFrequency: null,
      Combo_PremiumPayingTerm: null,
      Combo_Total_InstPremium_ST: null,
      Rider: null,
      Rider_Name: null,
      Rider_SA: null,
      LA_Proposer: "Same",
      PR_Mobileno: laMobile || "",
      PR_DOB: laDob || "",
      IS_LA_PR_Identical: "Y",
      Policy_Count: "0",
      Is_New_Script: "N",
      deferred_period: deferredPeriod,
      Maturity_date: formattedMaturityDate,
      Life_Assured_Name: laName || "",
      Life_Assured_DOB: laDob || "",
      Life_Assured_EmailID: laEmail || "",
    };
    setLoading(true);
    try {
      const redirectionData = await redirectPIVCLink(payload);
      const pivcAppNoFromResponse =
        redirectionData?.response?.Application_Number ||
        redirectionData?.Application_Number ||
        "";

      const urlFromResponse =
        redirectionData?.url ||
        redirectionData?.response?.url ||
        redirectionData?.response?.PIVC_URL ||
        redirectionData?.data?.url ||
        "";

      const cleanUrl = cleanPivcUrl(urlFromResponse);
      if (!cleanUrl) {
        setPivcError("Error redirecting to PIVC. Please try again.");
        return;
      }
      const pivcAppNoFromUrl = extractAppNumberFromPivcUrl(cleanUrl);

      setPivcError("");
      setPivcStatus(null);
      setPivcApplicationNumber(
        String(
          pivcAppNoFromResponse || pivcAppNoFromUrl || applicationNumber || "",
        ).trim(),
      );
      setIsVerified(false);
      const safePivcUrl = getSafeRedirectUrl(cleanUrl);
      if (!safePivcUrl) {
        setPivcError("Blocked unsafe PIVC redirect URL.");
        return;
      }
      window.open(safePivcUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      logError(error);
      setPivcError(
        "Please complete Pre-Issuance Verification to proceed with OTP",
      );
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async (openModal = false) => {
    try {
      setLoadingOtp(true);
      setPivcError("");

      const { mobile, email } = await resolveLeadContact(
        applicationNumber,
        leadData,
        dispatch,
        quoteData,
      );

      if (!mobile && !email) {
        setPivcError(
          "Mobile number or email is required to send OTP. Please update Life Assured details.",
        );
        return;
      }

      const response = await generateOtpAction({
        mobile: mobile || undefined,
        email: email || undefined,
        applicationNumber,
        customerName: leadData?.fullName || leadData?.name,
      })();

      if (response?.status === "success") {
        otpIdentifierRef.current = resolvePreferredOtpIdentifier({
          mobile,
          email,
        });
        setOtpError("");
        if (openModal) {
          setShowOtpModal(true);
        }

        setResendTimer(30);
        setResendDisabled(true);
        setOtpValue("");
      }
    } catch (error) {
      logError("OTP generation failed", error);
      const data = error?.response?.data;
      const parsed =
        typeof data === "string"
          ? (() => {
              try {
                return JSON.parse(data);
              } catch {
                return null;
              }
            })()
          : data;
      const msg =
        parsed?.errorMessage ||
        parsed?.error ||
        parsed?.message ||
        (typeof data === "string" ? data : null) ||
        "OTP could not be sent. Please try again.";
      setPivcError(msg);
    } finally {
      setLoadingOtp(false);
    }
  };

  const handleOtpValidation = async () => {
    if (missingDocs.length > 0) {
      setShowDocError(true);
      return;
    }
    setShowDocError(false);

    const resolvePivcStatus = (policyStatusResp) => {
      const responseObj = policyStatusResp?.response || {};
      const statusSource = String(responseObj?.PIVC_Status || "").trim();
      const isCompleted = /completed/i.test(statusSource);

      return {
        rawStatus: statusSource,
        isCompleted,
      };
    };
    try {
      // Call PIVC status only when user clicks "Validate with OTP".
      const appNoForPolicyStatus = String(
        pivcApplicationNumber || applicationNumber || "",
      ).trim();
      if (!appNoForPolicyStatus) {
        setPivcError("Application number is missing. Please try again.");
        return;
      }

      const policyStatusResp = await fetchPolicyStatus(appNoForPolicyStatus)();
      const policyStatus = policyStatusResp?.response?.Policy_Status;
      dispatch(journeyActions.setPolicyStatusData(policyStatus));
      const { rawStatus, isCompleted } = resolvePivcStatus(policyStatusResp);
      const statusNow = rawStatus || "Submitted";
      setPivcStatus(statusNow);

      if (!isCompleted) {
        setIsVerified(false);
        setPivcError(
          "Please complete Pre-Issuance Verification to proceed with OTP",
        );
        return;
      }

      setIsVerified(true);
      setPivcError("");
      setOtpError("");

      const documentsUploaded = JSON.stringify(
        Object.keys(DOC_LABELS).reduce((acc, key) => {
          const file =
            key === "addressProof"
              ? getDocForRequiredKey("addressProof")
              : documents?.[key];
          acc[key] = {
            uploaded: Boolean(file),
            fileName: file?.name || null,
          };
          return acc;
        }, {}),
      );

      await saveProposalSummary(applicationNumber, {
        documentsUploaded,
        pivcStatus: statusNow,
        summaryStatus: isCompleted ? "PIVC_Completed" : "Submitted",
      });
    } catch (e) {
      logError("Failed saving proposal summary before OTP", e);
      setPivcError("Unable to proceed. Please try again.");
      return;
    }

    sendOtp(true);
  };

  const handleResendOtp = () => {
    if (resendDisabled) return;
    sendOtp(false);
  };

  const handleVerifyOtp = async () => {
    if (otpValue.length !== 6) return;
    if (!isVerified) {
      setPivcError(
        "Please complete Pre-Issuance Verification to proceed with OTP",
      );
      return;
    }

    try {
      setCompletingJourney(true);

      setOtpError("");

      const verificationIdentifier =
        otpIdentifierRef.current ||
        resolvePreferredOtpIdentifier({
          mobile: displayMobile,
          email: displayEmail,
        });

      if (!verificationIdentifier) {
        setOtpError(
          "Unable to verify OTP. Please request a new OTP and try again.",
        );
        return;
      }

      otpIdentifierRef.current = verificationIdentifier;

      const response = await verifyOtp(verificationIdentifier, otpValue);

      if (response?.status === "success") {
        await saveJourneyCompletion(applicationNumber, {
          proposalFormStatus: pivcStatus || "Submitted",
          reverseFeedStatus: JOURNEY_STAGE.THANK_YOU_PAGE_REVERSE_FEED,
          otpVerificationToken: response?.verificationToken,
        });

        setShowOtpModal(false);
        setOtpValue("");
        if (paymentStatus === "PAY_LAST") {
          if (!caseId) {
            logWarning(
              "[ProposalReview] Missing caseId on PAY_LAST OTP verify",
            );
            setShowThankYou(true);
            return;
          }

          const paymentData = {
            applicationNumber,
            customerName,
            premiumAmount: totalPayableAmount,
            mobileNumber: displayMobile,
            emailAddress: displayEmail,
            productId: biApiProductId || undefined,
            paymentFlowMode: paymentStatus,
            transactionStatus: "INITIATED",
          };

          await updateJourneyStage(
            applicationNumber,
            caseId,
            JOURNEY_STAGE.PAYMENT_GATEWAY_REDIRECTION,
            {
              paymentData,
              paymentTypeBizCode: PAYMENT_TYPE_BIZ_CODE.ONLINE,
            },
          );

          if (onPayment) {
            const payResult = await onPayment(paymentData);
            if (payResult?.alreadyPaid) {
              setShowThankYou(true);
            }
          }
        } else {
          setShowThankYou(true);
        }
      } else {
        setOtpError(
          response?.message ||
            "OTP verification failed. Please re-enter the OTP and try again.",
        );
      }
    } catch (err) {
      logError("OTP verification failed", err);
      const data = err?.response?.data;
      const parsed =
        typeof data === "string"
          ? (() => {
              try {
                return JSON.parse(data);
              } catch {
                return null;
              }
            })()
          : data;
      setOtpError(
        parsed?.errorMessage ||
          parsed?.error ||
          parsed?.message ||
          (typeof data === "string" ? data : null) ||
          "OTP verification failed. Please re-enter the OTP and try again.",
      );
    } finally {
      setCompletingJourney(false);
    }
  };

  useEffect(() => {
    if (isResumingOrNotReady) return;

    if (resendTimer === 0) {
      setResendDisabled(false);
      return;
    }

    const interval = setInterval(() => {
      setResendTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [resendTimer, isResumingOrNotReady]);

  if (isResumingOrNotReady || completingJourney || loading || loadingOtp) {
    return (
      <div className="quote-premium-loader-overlay" aria-live="polite">
        <BrandLoader />
      </div>
    );
  }

  if (showThankYou) {
    return <ThankYouPage />;
  }

  const headerData =
    activeTab === "primary"
      ? primaryAnnuitant
      : activeTab === "secondary"
        ? secondaryAnnuitant
        : life;
  return (
    <>
      <div className="proposal-review insurance-journey-summary">
        <header className="sumarry-header">
          <div className="sumarry-header-wraper">
            <div className="header-left">
              <button
                class="back-button"
                onClick={() => navigate(-1)}
                aria-label="Go back"
              >
                <svg
                  width="18"
                  height="14"
                  viewBox="0 0 18 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17 7H1M1 7L7 13M1 7L7 1"
                    stroke="#252527"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ></path>
                </svg>
              </button>
              <h6 className="header-title m-0">Review before Confirming</h6>
            </div>

            <button
              className="help-button btn secondary-button regular"
              onClick={() => setShowHelpModal(true)}
            >
              <svg
                width="20"
                height="21"
                viewBox="0 0 20 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.6772 2.87806C15.3151 1.50799 13.5769 0.573101 11.683 0.191867C9.789 -0.189367 7.82449 0.000214264 6.03838 0.736587C4.25227 1.47296 2.72496 2.72298 1.64998 4.32823C0.575006 5.93349 0.000755732 7.82174 0 9.75369V15.0037C0 15.6004 0.237053 16.1727 0.65901 16.5947C1.08097 17.0166 1.65326 17.2537 2.25 17.2537H3.75C4.34674 17.2537 4.91903 17.0166 5.34099 16.5947C5.76295 16.1727 6 15.6004 6 15.0037V11.2537C6 10.657 5.76295 10.0847 5.34099 9.6627C4.91903 9.24074 4.34674 9.00369 3.75 9.00369H1.53375C1.67839 7.43988 2.26578 5.94969 3.22709 4.70779C4.18839 3.46588 5.48377 2.52372 6.96141 1.99172C8.43904 1.45972 10.0377 1.35993 11.57 1.70404C13.1024 2.04815 14.5048 2.82191 15.6131 3.93463C16.9677 5.29616 17.8009 7.09031 17.9672 9.00369H15.75C15.1533 9.00369 14.581 9.24074 14.159 9.6627C13.7371 10.0847 13.5 10.657 13.5 11.2537V15.0037C13.5 15.6004 13.7371 16.1727 14.159 16.5947C14.581 17.0166 15.1533 17.2537 15.75 17.2537H18C18 17.8504 17.7629 18.4227 17.341 18.8447C16.919 19.2666 16.3467 19.5037 15.75 19.5037H10.5C10.3011 19.5037 10.1103 19.5827 9.96967 19.7234C9.82902 19.864 9.75 20.0548 9.75 20.2537C9.75 20.4526 9.82902 20.6434 9.96967 20.784C10.1103 20.9247 10.3011 21.0037 10.5 21.0037H15.75C16.7446 21.0037 17.6984 20.6086 18.4017 19.9053C19.1049 19.2021 19.5 18.2483 19.5 17.2537V9.75369C19.5049 8.47829 19.2581 7.21446 18.7737 6.03462C18.2893 4.85477 17.5768 3.7821 16.6772 2.87806ZM3.75 10.5037C3.94891 10.5037 4.13968 10.5827 4.28033 10.7234C4.42098 10.864 4.5 11.0548 4.5 11.2537V15.0037C4.5 15.2026 4.42098 15.3934 4.28033 15.534C4.13968 15.6747 3.94891 15.7537 3.75 15.7537H2.25C2.05109 15.7537 1.86032 15.6747 1.71967 15.534C1.57902 15.3934 1.5 15.2026 1.5 15.0037V10.5037H3.75ZM15.75 15.7537C15.5511 15.7537 15.3603 15.6747 15.2197 15.534C15.079 15.3934 15 15.2026 15 15.0037V11.2537C15 11.0548 15.079 10.864 15.2197 10.7234C15.3603 10.5827 15.5511 10.5037 15.75 10.5037H18V15.7537H15.75Z"
                  fill="#97262A"
                ></path>
              </svg>
              Need Help?
            </button>
          </div>
        </header>

        <div className="review-container summary-content">
          <div className="custom-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                className={activeTab === tab.id ? "tab active" : "tab"}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="summary-wraper">
            <div className="review-left summary-left-panel">
              <div className="proposal-number">
                <span className="proposal-label">Proposal Number:</span>{" "}
                <span className="proposal-value">
                  {applicationNumber || "-"}
                </span>
              </div>

              <div className="review-card-header">
                <h2 className="bold">
                  {headerData?.fullName || displayName || "-"}
                </h2>

                <p>
                  {activeTab === "proposer" ? (
                    <>
                      {displayGender || "-"} | {displayEmail || "-"} |{" "}
                      {displayMobile || "-"}
                    </>
                  ) : (
                    <>
                      {leadData?.annuitantGender || "-"} |{" "}
                      {headerData?.email || "-"}
                    </>
                  )}
                </p>

                <div className="review-card-body">
                  {/* <p>
                    {s ? `${s} | ` : ""}
                    {calculateAge(displayDob)} years ({displayDob || "-"})
                  </p> */}

                  <p>
                    {s ? `${s} | ` : ""}

                    {(() => {
                      const dob =
                        activeTab === "primary"
                          ? leadData?.annuitantDateOfBirth
                          : activeTab === "secondary"
                            ? secondaryAnnuitantDob
                            : displayDob;

                      return `${calculateAge(dob)} years (${formatDOB(dob)})`;
                    })()}
                  </p>
                  {(() => {
                    // Only fall back to the proposer's address when this
                    // tab's own "same as proposer" toggle was actually
                    // selected — matching shouldHideBankAccountProof's
                    // pattern above. Previously this fell back whenever any
                    // one of the 4 fields was missing, which meant a
                    // genuinely-different PA/SA address with, say, a blank
                    // pinCode also silently showed the proposer's address
                    // instead of this tab's own.
                    const usesProposerAddress =
                      activeTab === "primary"
                        ? paUsesProposerAddress
                        : activeTab === "secondary"
                          ? saUsesProposerAddress
                          : false;
                    const addressSource = usesProposerAddress
                      ? mergedStageData
                      : headerData;
                    return (
                      <p>
                        {addressSource.addressLine1 || "-"},{" "}
                        {addressSource?.addressLine2 || "-"},{" "}
                        {addressSource.city || "-"},{" "}
                        {addressSource.state || "-"} -{" "}
                        {addressSource.pinCode || "-"}
                      </p>
                    );
                  })()}
                </div>
              </div>

              {activeTab === "proposer" && (
                <div className="review-card plan-details-wraper">
                  <div className="plan-details-card">
                    <div className="plan-details-header">
                      Personal Details
                      <button
                        className="edit-btn"
                        onClick={() =>
                          navigate("/proposal", {
                            state: { tab: "proposer", section: "personal" },
                            replace: true,
                          })
                        }
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 13 13"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12.2069 3.0861L9.41438 0.292974C9.32152 0.200091 9.21127 0.126411 9.08993 0.0761423C8.96859 0.0258733 8.83853 0 8.70719 0C8.57585 0 8.4458 0.0258733 8.32446 0.0761423C8.20312 0.126411 8.09287 0.200091 8 0.292974L0.29313 8.00047C0.199867 8.093 0.125926 8.20313 0.0756045 8.32448C0.025283 8.44584 -0.000414649 8.57598 5.05934e-06 8.70735V11.5005C5.05934e-06 11.7657 0.105362 12.02 0.292898 12.2076C0.480435 12.3951 0.734788 12.5005 1 12.5005H3.79313C3.9245 12.5009 4.05464 12.4752 4.17599 12.4249C4.29735 12.3746 4.40748 12.3006 4.5 12.2073L12.2069 4.50047C12.2998 4.40761 12.3734 4.29736 12.4237 4.17602C12.474 4.05468 12.4999 3.92463 12.4999 3.79329C12.4999 3.66195 12.474 3.53189 12.4237 3.41055C12.3734 3.28921 12.2998 3.17896 12.2069 3.0861ZM3.79313 11.5005H1V8.70735L6.5 3.20735L9.29313 6.00047L3.79313 11.5005ZM10 5.29297L7.20688 2.50047L8.70688 1.00047L11.5 3.79297L10 5.29297Z"
                            fill="#97262A"
                          />
                        </svg>
                      </button>
                    </div>

                    <div className="review-grid plan-details-grid">
                      <div className="plan-detail-item ">
                        <span className="detail-label">Father Name</span>
                        <span className="detail-value">
                          {mergedStageData?.fatherName || "-"}
                        </span>
                      </div>
                      <div class="divider"></div>
                      <div className="plan-detail-item">
                        <span className="detail-label">Mother Name</span>
                        <span className="detail-value">
                          {mergedStageData?.motherName || "-"}
                        </span>
                      </div>
                      <div class="divider"></div>
                      <div className="plan-detail-item">
                        <span className="detail-label">Nationality</span>
                        <span className="detail-value">
                          {mergedStageData?.citizenship || "-"}
                        </span>
                      </div>
                      <div class="divider"></div>
                      <div className="plan-detail-item">
                        <span className="detail-label">Marital Status</span>
                        <span className="detail-value">
                          {mergedStageData?.maritalStatusLabel || "-"}
                        </span>
                      </div>

                      <div class="divider"></div>
                      <div className="plan-detail-item">
                        <span className="detail-label">Job Description</span>
                        <span className="detail-value">
                          {mergedStageData?.jobDiscription || "-"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "proposer" && (
                <div className="review-card plan-details-wraper">
                  <div className="plan-details-card">
                    <div className=" plan-details-header">
                      Bank Details
                      <button
                        className="edit-btn"
                        onClick={() =>
                          navigate("/proposal", {
                            state: { tab: "proposer", section: "bank" },
                            replace: true,
                          })
                        }
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 13 13"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12.2069 3.0861L9.41438 0.292974C9.32152 0.200091 9.21127 0.126411 9.08993 0.0761423C8.96859 0.0258733 8.83853 0 8.70719 0C8.57585 0 8.4458 0.0258733 8.32446 0.0761423C8.20312 0.126411 8.09287 0.200091 8 0.292974L0.29313 8.00047C0.199867 8.093 0.125926 8.20313 0.0756045 8.32448C0.025283 8.44584 -0.000414649 8.57598 5.05934e-06 8.70735V11.5005C5.05934e-06 11.7657 0.105362 12.02 0.292898 12.2076C0.480435 12.3951 0.734788 12.5005 1 12.5005H3.79313C3.9245 12.5009 4.05464 12.4752 4.17599 12.4249C4.29735 12.3746 4.40748 12.3006 4.5 12.2073L12.2069 4.50047C12.2998 4.40761 12.3734 4.29736 12.4237 4.17602C12.474 4.05468 12.4999 3.92463 12.4999 3.79329C12.4999 3.66195 12.474 3.53189 12.4237 3.41055C12.3734 3.28921 12.2998 3.17896 12.2069 3.0861ZM3.79313 11.5005H1V8.70735L6.5 3.20735L9.29313 6.00047L3.79313 11.5005ZM10 5.29297L7.20688 2.50047L8.70688 1.00047L11.5 3.79297L10 5.29297Z"
                            fill="#97262A"
                          />
                        </svg>
                      </button>
                    </div>

                    <div className="review-grid plan-details-grid">
                      <div className="plan-detail-item">
                        <span className="detail-label">Bank Name</span>
                        <span className="detail-value">
                          {mergedStageData?.bankName || "-"}
                        </span>
                      </div>
                      <div class="divider"></div>
                      <div className="plan-detail-item">
                        <span className="detail-label">Branch Name</span>
                        <span className="detail-value">
                          {mergedStageData?.branchName || "-"}
                        </span>
                      </div>
                      <div class="divider"></div>
                      <div className="plan-detail-item">
                        <span className="detail-label">Account Number</span>
                        <span className="detail-value">
                          {mergedStageData?.accountNumber || "-"}
                        </span>
                      </div>
                      <div class="divider"></div>
                      <div className="plan-detail-item">
                        <span className="detail-label">IFSC Code</span>
                        <span className="detail-value">
                          {mergedStageData?.ifscCode || "-"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {activeTab === "primary" && (
                <div className="review-card plan-details-wraper">
                  <div className="plan-details-card">
                    <div className=" plan-details-header">
                      Primary Annuitant Details
                      <button
                        className="edit-btn"
                        onClick={() =>
                          navigate("/proposal", {
                            state: {
                              tab: "annuitant",
                              section: "primaryAnnuitant",
                            },
                            replace: true,
                          })
                        }
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 13 13"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12.2069 3.0861L9.41438 0.292974C9.32152 0.200091 9.21127 0.126411 9.08993 0.0761423C8.96859 0.0258733 8.83853 0 8.70719 0C8.57585 0 8.4458 0.0258733 8.32446 0.0761423C8.20312 0.126411 8.09287 0.200091 8 0.292974L0.29313 8.00047C0.199867 8.093 0.125926 8.20313 0.0756045 8.32448C0.025283 8.44584 -0.000414649 8.57598 5.05934e-06 8.70735V11.5005C5.05934e-06 11.7657 0.105362 12.02 0.292898 12.2076C0.480435 12.3951 0.734788 12.5005 1 12.5005H3.79313C3.9245 12.5009 4.05464 12.4752 4.17599 12.4249C4.29735 12.3746 4.40748 12.3006 4.5 12.2073L12.2069 4.50047C12.2998 4.40761 12.3734 4.29736 12.4237 4.17602C12.474 4.05468 12.4999 3.92463 12.4999 3.79329C12.4999 3.66195 12.474 3.53189 12.4237 3.41055C12.3734 3.28921 12.2998 3.17896 12.2069 3.0861ZM3.79313 11.5005H1V8.70735L6.5 3.20735L9.29313 6.00047L3.79313 11.5005ZM10 5.29297L7.20688 2.50047L8.70688 1.00047L11.5 3.79297L10 5.29297Z"
                            fill="#97262A"
                          />
                        </svg>
                      </button>
                    </div>
                    <div className="review-grid plan-details-grid">
                      {getAnnuitantDetails(primaryAnnuitant).map(
                        (item, index) => (
                          <React.Fragment key={index}>
                            <div className="plan-detail-item">
                              <span className="detail-label">{item.label}</span>
                              <span className="detail-value">
                                {item.value || "-"}
                              </span>
                            </div>

                            {index !==
                              getAnnuitantDetails(primaryAnnuitant).length -
                                1 && <div className="divider"></div>}
                          </React.Fragment>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "secondary" && (
                <div className="review-card plan-details-wraper">
                  <div className="plan-details-card">
                    <div className=" plan-details-header">
                      Secondary Annuitant Details
                      <button
                        className="edit-btn"
                        onClick={() =>
                          navigate("/proposal", {
                            state: {
                              tab: "annuitant",
                              section: "secondaryAnnuitant",
                            },
                            replace: true,
                          })
                        }
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 13 13"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12.2069 3.0861L9.41438 0.292974C9.32152 0.200091 9.21127 0.126411 9.08993 0.0761423C8.96859 0.0258733 8.83853 0 8.70719 0C8.57585 0 8.4458 0.0258733 8.32446 0.0761423C8.20312 0.126411 8.09287 0.200091 8 0.292974L0.29313 8.00047C0.199867 8.093 0.125926 8.20313 0.0756045 8.32448C0.025283 8.44584 -0.000414649 8.57598 5.05934e-06 8.70735V11.5005C5.05934e-06 11.7657 0.105362 12.02 0.292898 12.2076C0.480435 12.3951 0.734788 12.5005 1 12.5005H3.79313C3.9245 12.5009 4.05464 12.4752 4.17599 12.4249C4.29735 12.3746 4.40748 12.3006 4.5 12.2073L12.2069 4.50047C12.2998 4.40761 12.3734 4.29736 12.4237 4.17602C12.474 4.05468 12.4999 3.92463 12.4999 3.79329C12.4999 3.66195 12.474 3.53189 12.4237 3.41055C12.3734 3.28921 12.2998 3.17896 12.2069 3.0861ZM3.79313 11.5005H1V8.70735L6.5 3.20735L9.29313 6.00047L3.79313 11.5005ZM10 5.29297L7.20688 2.50047L8.70688 1.00047L11.5 3.79297L10 5.29297Z"
                            fill="#97262A"
                          />
                        </svg>
                      </button>
                    </div>
                    <div className="review-grid plan-details-grid">
                      {getAnnuitantDetails(secondaryAnnuitant).map(
                        (item, index) => (
                          <React.Fragment key={index}>
                            <div className="plan-detail-item">
                              <span className="detail-label">{item.label}</span>
                              <span className="detail-value">
                                {item.value || "-"}
                              </span>
                            </div>

                            {index !==
                              getAnnuitantDetails(secondaryAnnuitant).length -
                                1 && <div className="divider"></div>}
                          </React.Fragment>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              )}

              {(activeTab === "primary" || activeTab === "secondary") && (
                <div className="review-card plan-details-wraper">
                  <div className="plan-details-card">
                    <div className=" plan-details-header">
                      Bank Details
                      <button
                        className="edit-btn"
                        onClick={() =>
                          navigate("/proposal", {
                            state: {
                              tab: "annuitant",
                              section: `${activeTab}Annuitant`,
                            },
                            replace: true,
                          })
                        }
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 13 13"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12.2069 3.0861L9.41438 0.292974C9.32152 0.200091 9.21127 0.126411 9.08993 0.0761423C8.96859 0.0258733 8.83853 0 8.70719 0C8.57585 0 8.4458 0.0258733 8.32446 0.0761423C8.20312 0.126411 8.09287 0.200091 8 0.292974L0.29313 8.00047C0.199867 8.093 0.125926 8.20313 0.0756045 8.32448C0.025283 8.44584 -0.000414649 8.57598 5.05934e-06 8.70735V11.5005C5.05934e-06 11.7657 0.105362 12.02 0.292898 12.2076C0.480435 12.3951 0.734788 12.5005 1 12.5005H3.79313C3.9245 12.5009 4.05464 12.4752 4.17599 12.4249C4.29735 12.3746 4.40748 12.3006 4.5 12.2073L12.2069 4.50047C12.2998 4.40761 12.3734 4.29736 12.4237 4.17602C12.474 4.05468 12.4999 3.92463 12.4999 3.79329C12.4999 3.66195 12.474 3.53189 12.4237 3.41055C12.3734 3.28921 12.2998 3.17896 12.2069 3.0861ZM3.79313 11.5005H1V8.70735L6.5 3.20735L9.29313 6.00047L3.79313 11.5005ZM10 5.29297L7.20688 2.50047L8.70688 1.00047L11.5 3.79297L10 5.29297Z"
                            fill="#97262A"
                          />
                        </svg>
                      </button>
                    </div>

                    <div className="review-grid plan-details-grid">
                      <div className="plan-detail-item">
                        <span className="detail-label">Bank Name</span>
                        {headerData?.bankName ? (
                          <span className="detail-value">
                            {headerData.bankName || "-"}
                          </span>
                        ) : (
                          <span className="detail-value">
                            {mergedStageData?.bankName || "-"}
                          </span>
                        )}
                      </div>
                      <div class="divider"></div>
                      <div className="plan-detail-item">
                        <span className="detail-label">Branch Name</span>
                        {headerData?.branchName ? (
                          <span className="detail-value">
                            {headerData.branchName || "-"}
                          </span>
                        ) : (
                          <span className="detail-value">
                            <span className="detail-value">
                              {mergedStageData?.branchName || "-"}
                            </span>
                          </span>
                        )}
                      </div>
                      <div class="divider"></div>
                      <div className="plan-detail-item">
                        <span className="detail-label">Account Number</span>
                        {headerData?.accountNumber ? (
                          <span className="detail-value">
                            {headerData.accountNumber || "-"}
                          </span>
                        ) : (
                          <span className="detail-value">
                            {mergedStageData?.accountNumber || "-"}
                          </span>
                        )}
                      </div>
                      <div class="divider"></div>
                      <div className="plan-detail-item">
                        <span className="detail-label">IFSC Code</span>

                        {headerData?.ifscCode ? (
                          <span className="detail-value">
                            {headerData.ifscCode || "-"}
                          </span>
                        ) : (
                          <span className="detail-value">
                            {mergedStageData?.ifscCode || "-"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {nominees.map((nom, index) => {
                const claimPct = getNomineeClaimPercentage(nom);
                return (
                  <div className="review-card plan-details-wraper">
                    <div className="plan-details-card">
                      <div className=" plan-details-header">
                        Nominee {index + 1} Details
                        <button
                          className="edit-btn"
                          onClick={() =>
                            navigate("/proposal", { state: { tab: "nominee" } })
                          }
                        >
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 13 13"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12.2069 3.0861L9.41438 0.292974C9.32152 0.200091 9.21127 0.126411 9.08993 0.0761423C8.96859 0.0258733 8.83853 0 8.70719 0C8.57585 0 8.4458 0.0258733 8.32446 0.0761423C8.20312 0.126411 8.09287 0.200091 8 0.292974L0.29313 8.00047C0.199867 8.093 0.125926 8.20313 0.0756045 8.32448C0.025283 8.44584 -0.000414649 8.57598 5.05934e-06 8.70735V11.5005C5.05934e-06 11.7657 0.105362 12.02 0.292898 12.2076C0.480435 12.3951 0.734788 12.5005 1 12.5005H3.79313C3.9245 12.5009 4.05464 12.4752 4.17599 12.4249C4.29735 12.3746 4.40748 12.3006 4.5 12.2073L12.2069 4.50047C12.2998 4.40761 12.3734 4.29736 12.4237 4.17602C12.474 4.05468 12.4999 3.92463 12.4999 3.79329C12.4999 3.66195 12.474 3.53189 12.4237 3.41055C12.3734 3.28921 12.2998 3.17896 12.2069 3.0861ZM3.79313 11.5005H1V8.70735L6.5 3.20735L9.29313 6.00047L3.79313 11.5005ZM10 5.29297L7.20688 2.50047L8.70688 1.00047L11.5 3.79297L10 5.29297Z"
                              fill="#97262A"
                            />
                          </svg>
                        </button>
                      </div>

                      <div className="review-grid plan-details-nomine-card">
                        <div className="nomine-card-top">
                          <h2 className="bold">{nom.fullName || "-"}</h2>
                          <p>
                            {nom.relationship || "-"} | +91{" "}
                            {maskPhone(nom.mobileNo)}{" "}
                            {nom?.emailId ? "| " + nom.emailId : ""}
                          </p>
                        </div>
                        <div className="nomine-card-bottom">
                          <p>
                            <span>
                              {claimPct != null
                                ? `${claimPct}% Claim`
                                : "- · Claim"}
                            </span>{" "}
                            | {calculateAge(nom.dateOfBirth)} years (
                            {nom.dateOfBirth || "-"})
                          </p>
                          <p>{formatNomineeAddressForReview(nom, life)}</p>
                        </div>
                      </div>

                      <div className="review-grid plan-details-grid mt-3">
                        <div className="plan-detail-item">
                          <span className="detail-label">Bank Name</span>
                          <span className="detail-value">
                            {nom.bankName || "-"}
                          </span>
                        </div>
                        <div class="divider"></div>
                        <div className="plan-detail-item">
                          <span className="detail-label">Branch Name</span>
                          <span className="detail-value">
                            {nom.branchName || "-"}
                          </span>
                        </div>
                        <div class="divider"></div>
                        <div className="plan-detail-item">
                          <span className="detail-label">Account Number</span>
                          <span className="detail-value">
                            {maskAccount(nom.accountNumber)}
                          </span>
                        </div>
                        <div className="plan-detail-item">
                          <span className="detail-label">IFSC Code</span>
                          <span className="detail-value">
                            {nom.ifscCode || "-"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="review-right">
              <div className="review-help-card">
                <svg
                  width="204"
                  height="150"
                  viewBox="0 0 204 150"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M181.908 149.72L90.2596 149.604C87.6192 149.601 85.4812 147.458 85.4844 144.817L85.6377 15.2951C85.6409 12.6543 87.7844 10.516 90.4252 10.5195L182.073 10.6346C184.714 10.6378 186.852 12.781 186.849 15.4218L186.695 144.944C186.692 147.585 184.549 149.723 181.908 149.72Z"
                    fill="#FF637A"
                  />
                  <path
                    d="M178.666 149.72L87.0174 149.604C84.377 149.601 82.239 147.458 82.2422 144.817L82.3955 15.2951C82.3987 12.6543 84.5419 10.516 87.183 10.5195L178.831 10.6346C181.472 10.6378 183.61 12.781 183.606 15.4218L183.453 144.944C183.449 147.585 181.306 149.723 178.666 149.72Z"
                    fill="#FFC5CE"
                  />
                  <path
                    d="M179.75 149.845C179.169 149.845 178.625 149.844 178.3 149.839L178.31 149.196C179.273 149.211 182.182 149.196 182.212 149.196C182.214 149.196 182.216 149.196 182.218 149.196C183.391 149.196 184.493 148.728 185.323 147.877C186.157 147.023 186.617 145.886 186.619 144.675L186.772 15.6968C186.775 13.2007 184.804 11.1678 182.378 11.1646L90.1875 11.0492L90.1885 10.4062L182.378 10.5217C185.159 10.5252 187.418 12.8471 187.415 15.6974L187.261 144.676C187.26 146.055 186.735 147.352 185.783 148.327C184.831 149.302 183.566 149.839 182.219 149.839C182.217 149.839 182.215 149.839 182.213 149.839C182.195 149.839 180.901 149.845 179.75 149.845Z"
                    fill="#34436C"
                  />
                  <path
                    d="M178.937 149.839L86.7472 149.724C83.9669 149.72 81.7074 147.398 81.7109 144.548L81.8643 15.5695C81.8659 14.1904 82.3908 12.8937 83.3427 11.919C84.2951 10.9434 85.5607 10.4062 86.9067 10.4062C86.9089 10.4062 86.9108 10.4062 86.9131 10.4062L179.103 10.5217C181.883 10.5252 184.143 12.8471 184.139 15.6974L184.112 38.2223L183.47 38.2216L183.497 15.6965C183.499 13.2007 181.528 11.1678 179.102 11.1646L86.9121 11.0492C85.7369 11.0379 84.6343 11.5162 83.8027 12.3681C82.9688 13.2225 82.5088 14.3595 82.5072 15.5701L82.3539 144.548C82.351 147.045 84.3225 149.078 86.7482 149.081L178.938 149.196L178.937 149.839Z"
                    fill="#34436C"
                  />
                  <path
                    d="M184.08 46.1016H183.438V91.0786H184.08V46.1016Z"
                    fill="#34436C"
                  />
                  <g opacity="0.36">
                    <path
                      d="M178.347 22.1427L92.6523 22.0312L92.4948 143.255L178.189 143.367L178.347 22.1427Z"
                      fill="#FF637A"
                    />
                  </g>
                  <path
                    d="M175.851 19.5723L90.1562 19.4609L89.9987 140.685L175.693 140.796L175.851 19.5723Z"
                    fill="white"
                  />
                  <path
                    d="M166.742 30.2578H99.1133V49.6272H166.742V30.2578Z"
                    fill="#FF9CAB"
                  />
                  <path
                    d="M117.021 56.6719H98.6367V75.0567H117.021V56.6719Z"
                    fill="#FFDFE4"
                  />
                  <path
                    d="M117.021 85.2344H98.6367V103.619H117.021V85.2344Z"
                    fill="#FFDFE4"
                  />
                  <path
                    d="M117.021 113.797H98.6367V132.182H117.021V113.797Z"
                    fill="#FFDFE4"
                  />
                  <path
                    d="M117.339 75.3778H111.785V74.7349H116.696V56.6719H117.339V75.3778Z"
                    fill="#34436C"
                  />
                  <path
                    d="M117.341 103.941H107.109V103.298H116.698V85.2344H117.341V103.941Z"
                    fill="#34436C"
                  />
                  <path
                    d="M117.343 132.503H98.6367V131.86H116.7V113.797H117.343V132.503Z"
                    fill="#34436C"
                  />
                  <path
                    d="M142.131 62.332H120.953V62.9749H142.131V62.332Z"
                    fill="#34436C"
                  />
                  <path
                    d="M152.636 68.7617H120.953V69.4046H152.636V68.7617Z"
                    fill="#34436C"
                  />
                  <path
                    d="M146.971 90.8906H120.953V91.5335H146.971V90.8906Z"
                    fill="#34436C"
                  />
                  <path
                    d="M166.738 97.3203H120.953V97.9632H166.738V97.3203Z"
                    fill="#34436C"
                  />
                  <path
                    d="M146.971 119.453H120.953V120.096H146.971V119.453Z"
                    fill="#34436C"
                  />
                  <path
                    d="M166.738 125.883H120.953V126.526H166.738V125.883Z"
                    fill="#34436C"
                  />
                  <path
                    d="M106.853 129.124L101.281 122.087L103.045 120.69L106.83 125.47L112.599 117.992L114.38 119.367L106.853 129.124Z"
                    fill="white"
                  />
                  <g opacity="0.32">
                    <path
                      d="M103.529 83.2499C103.516 83.3013 103.504 83.3495 103.488 83.4009C98.19 105.035 87.7565 129.54 67.2762 144.989C65.0575 146.664 62.7179 148.232 60.2578 149.679H52.6556C1.85832 119.845 4.12458 38.2619 4.12458 38.2619C13.0347 37.3618 22.989 33.5686 33.3753 28.5732C40.879 24.9632 48.9737 23.1984 57.0551 23.2884C64.7241 23.3688 72.3802 25.1143 79.5066 28.5314C89.9691 33.5493 100.003 37.3586 108.938 38.2619C108.938 38.2619 109.471 58.922 103.529 83.2499Z"
                      fill="#647AA0"
                    />
                  </g>
                  <path
                    d="M100.912 80.9998C100.9 81.0519 100.888 81.101 100.872 81.1502C95.5052 102.784 84.9405 127.29 64.2007 142.74C60.8245 145.256 57.175 147.532 53.2365 149.517C52.7604 149.277 52.2904 149.035 51.8233 148.783C-2.11976 120.144 0.24585 36.0118 0.24585 36.0118C9.26851 35.1118 19.3481 31.3176 29.8669 26.3225C37.4671 22.7128 45.6636 20.9493 53.8476 21.0384C61.614 21.1181 69.3678 22.8633 76.5842 26.2823C87.1798 31.299 97.3423 35.1086 106.39 36.0115C106.39 36.0122 106.93 56.6719 100.912 80.9998Z"
                    fill="#E35055"
                  />
                  <path
                    d="M100.913 81.0002C100.901 81.0522 100.889 81.1014 100.873 81.1506C95.3772 102.72 84.7447 126.996 64.2016 142.74C60.8253 145.256 57.1758 147.533 53.2374 149.517C52.7613 149.278 52.2913 149.035 51.8242 148.783C57.8178 136.532 94.0071 57.861 53.8488 21.0391C61.6152 21.1188 69.369 22.864 76.5854 26.283C87.181 31.2997 97.3435 35.1092 106.391 36.0122C106.391 36.0122 106.931 56.6723 100.913 81.0002Z"
                    fill="#CF3339"
                  />
                  <path
                    d="M53.2536 138.551L52.892 138.369C42.4581 133.113 33.6691 124.922 26.7694 114.023C21.2673 105.332 16.9492 94.9104 13.9349 83.0474C8.81116 62.8811 9.23998 45.2649 9.24513 45.0891L9.26506 44.3825L9.96841 44.3121C16.3689 43.6718 23.9589 41.1846 33.8543 36.4846C46.179 30.6312 60.3131 30.6193 72.6333 36.4515C82.602 41.1711 90.2398 43.6689 96.6702 44.3121L97.3749 44.3828L97.3935 45.091C97.3984 45.2665 97.7941 62.8827 92.6376 83.0487C89.6043 94.9117 85.2707 105.333 79.7577 114.024C72.8445 124.922 64.0488 133.114 53.6149 138.37L53.2536 138.551ZM10.8428 45.8329C10.8222 49.2796 11 64.9898 15.5026 82.6893C20.1428 100.93 30.4575 125.068 53.2536 136.75C76.0507 125.067 86.4007 100.929 91.0708 82.688C95.6014 64.9911 95.8091 49.2825 95.7946 45.8329C89.3295 45.0637 81.7225 42.5325 71.9457 37.9042C60.0634 32.2787 46.4307 32.2906 34.5441 37.9363C24.8381 42.546 17.2787 45.0669 10.8428 45.8329Z"
                    fill="white"
                  />
                  <path
                    d="M78.7392 72.9922V85.6152H60.9707V103.381H48.3506V85.6152H30.582V72.9922H48.3506V55.2266H60.9707V72.9922H78.7392Z"
                    fill="white"
                  />
                  <path
                    d="M53.1086 149.805C52.584 149.542 52.1188 149.3 51.6874 149.067C32.2203 138.732 17.8637 120.129 9.01784 93.777C1.93839 72.6862 0.329172 52.2284 0 41.9109L0.642596 41.8906C1.24661 60.8257 6.73453 124.474 51.9909 148.501C52.4191 148.731 52.8784 148.97 53.3976 149.231L53.1086 149.805Z"
                    fill="#34436C"
                  />
                  <path
                    d="M64.4112 142.998L64.0273 142.482C81.379 129.556 93.6766 108.896 100.579 81.073C100.597 81.0125 100.608 80.9711 100.618 80.9267C104.615 64.7715 105.695 50.1272 105.983 42.8359L106.625 42.8613C106.336 50.1774 105.252 64.8706 101.243 81.0775C101.23 81.1337 101.215 81.1906 101.198 81.2469C98.1311 93.6108 93.8914 104.778 88.6018 114.419C82.1006 126.268 73.9617 135.883 64.4112 142.998Z"
                    fill="#34436C"
                  />
                  <path
                    d="M101.986 35.6915C94.8583 34.3414 86.7479 31.4444 76.4616 26.574C69.3314 23.1958 61.727 21.442 53.859 21.361C45.6911 21.2748 37.4425 23.0885 30.0194 26.6139C19.9777 31.3824 12.0435 34.244 5.04986 35.6195L4.92578 34.9888C11.8654 33.6239 19.7514 30.7784 29.7436 26.0333C37.2541 22.4661 45.6007 20.6274 53.8657 20.718C61.8273 20.8 69.522 22.5748 76.7364 25.9932C86.972 30.8394 95.0331 33.7207 102.105 35.0602L101.986 35.6915Z"
                    fill="#34436C"
                  />
                  <path
                    d="M140.896 8.26527V8.18555C140.903 3.31966 136.421 -0.522402 131.289 0.480545C127.579 1.20543 124.96 4.50101 124.955 8.22638V8.24502L114.499 8.23184C113.193 8.23024 112.132 9.27208 112.13 10.5592L112.117 20.9063C112.116 22.1934 113.173 23.2378 114.48 23.2394L151.332 23.2854C152.639 23.287 153.7 22.2451 153.701 20.958L153.715 10.6109C153.716 9.32383 152.658 8.27942 151.352 8.27781L140.896 8.26527ZM133.455 9.11714C131.783 9.48328 130.323 8.05343 130.692 6.40661C130.878 5.57596 131.558 4.90508 132.4 4.7196C134.073 4.35153 135.534 5.79038 135.162 7.43688C134.976 8.26528 134.296 8.93294 133.455 9.11714Z"
                    fill="#FF637A"
                  />
                  <path
                    d="M120.863 23.5679L114.48 23.5598C113.764 23.5592 113.09 23.283 112.583 22.7825C112.075 22.2807 111.796 21.614 111.797 20.9049L111.81 10.5578L112.453 10.5588L112.44 20.9059C112.439 21.4414 112.651 21.9455 113.035 22.3251C113.421 22.7063 113.935 22.9163 114.482 22.9169L120.864 22.9249L120.863 23.5679ZM132.932 9.49413C132.231 9.49413 131.559 9.22315 131.061 8.72199C130.439 8.09708 130.184 7.20439 130.379 6.33453C130.594 5.37562 131.361 4.61763 132.332 4.40386C133.236 4.2052 134.156 4.46976 134.795 5.11171C135.419 5.73951 135.674 6.63445 135.477 7.50592C135.261 8.46194 134.495 9.21704 133.524 9.42952C133.327 9.4726 133.128 9.49413 132.932 9.49413ZM132.923 4.98216C132.773 4.98216 132.622 4.99855 132.47 5.03166C131.753 5.1895 131.165 5.76973 131.007 6.47501C130.858 7.13914 131.043 7.79266 131.517 8.2681C131.999 8.75285 132.697 8.95216 133.387 8.80107C134.103 8.6442 134.691 8.06686 134.85 7.36416C135 6.69842 134.814 6.04265 134.338 5.56496C133.962 5.18693 133.454 4.98216 132.923 4.98216ZM151.353 8.59823L140.576 8.58473V8.1842C140.579 5.94332 139.579 3.83295 137.834 2.39379C136.049 0.921192 133.685 0.338066 131.351 0.794536C127.837 1.48149 125.283 4.60638 125.278 8.22534L125.278 8.5468L124.956 8.56512L114.5 8.55194L114.501 7.90903L124.642 7.92189C124.791 4.11776 127.516 0.889366 131.228 0.163837C133.752 -0.329278 136.309 0.302708 138.244 1.8981C140.075 3.40799 141.149 5.6 141.216 7.9431L151.354 7.95564L151.353 8.59823Z"
                    fill="#34436C"
                  />
                  <path
                    d="M142.067 22.9648H125.734V23.6078H142.067V22.9648Z"
                    fill="#34436C"
                  />
                  <path
                    d="M203.979 149.355H6.28906V149.998H203.979V149.355Z"
                    fill="#34436C"
                  />
                  <path
                    d="M169.817 59.3666C169.811 59.8356 169.727 60.3088 169.581 60.7804C168.27 65.0008 161.98 69.0807 161.98 69.0807C161.98 69.0807 155.688 65.0008 154.377 60.7804C154.231 60.3088 154.147 59.8356 154.141 59.3666C154.141 56.9811 156.074 55.0469 158.46 55.0469C159.912 55.0469 161.197 55.7631 161.98 56.8615C162.762 55.7631 164.045 55.0469 165.497 55.0469C167.884 55.0469 169.817 56.9811 169.817 59.3666Z"
                    fill="#E35055"
                  />
                  <path
                    d="M158.401 66.707C156.081 64.6304 154.623 62.6682 154.065 60.8748C153.913 60.3852 153.83 59.8809 153.815 59.3746C153.777 58.0772 154.207 56.9415 155.058 56.0902C155.936 55.2123 157.248 54.6887 158.465 54.7244C159.751 54.7617 161.132 55.4113 161.972 56.3439C162.849 55.3229 164.136 54.724 165.493 54.724C168.052 54.724 170.134 56.8061 170.134 59.3652H169.491C169.491 57.1607 167.697 55.367 165.493 55.367C164.203 55.367 162.986 55.9951 162.237 57.0469L161.976 57.4143L161.714 57.0469C161.035 56.0938 159.691 55.403 158.447 55.3666C157.391 55.3384 156.27 55.7878 155.513 56.5445C154.79 57.2674 154.425 58.2395 154.458 59.3556C154.471 59.8034 154.546 60.2502 154.68 60.6835C155.194 62.3403 156.629 64.2575 158.83 66.2274L158.401 66.707Z"
                    fill="#34436C"
                  />
                  <path
                    d="M166.455 65.8621L166.004 65.4037C166.881 64.5409 167.607 63.6929 168.161 62.8828L168.691 63.2457C168.114 64.0899 167.362 64.97 166.455 65.8621Z"
                    fill="#34436C"
                  />
                </svg>
                <h6 className="large-body bold">
                  {TEXT_CONTENT.PRO_SIDE_HEADING}
                </h6>
                <p className="small-body regular">
                  {TEXT_CONTENT.PRO_SIDE_SUBHEAD}
                </p>
                <p className="small-body regular">
                  {TEXT_CONTENT.PRO_SIDE_SUBHEADD}
                </p>
              </div>

              <div className="review-upload-card">
                <h6 className="large-body bold">Upload Documents</h6>
                <p>{TEXT_CONTENT.UPLOAD_DOC}</p>
                <p>
                  You can upload jpg, png or pdf file formats with a maximum
                  file size limit of {MAX_DOCUMENT_UPLOAD_MB}mb.
                </p>
                {documentUploadError ? (
                  <p
                    ref={documentUploadErrorRef}
                    className="pivc-error"
                    role="alert"
                  >
                    {documentUploadError}
                  </p>
                ) : null}
                {Object.keys(DOC_LABELS)
                  .filter((key) => {
                    if (
                      key === "bankAccountProof" &&
                      shouldHideBankAccountProof(activeTab)
                    ) {
                      return false;
                    }
                    return true;
                  })
                  .map((key) => {
                    const uploaded =  getDocForRequiredKey(key);

                    return (
                      <div key={key} className="upload-doc-row">
                        <label>{DOC_LABELS[key]}</label>

                        <div className="upload-doc-row__body">
                          <div
                            className={`upload-box ${
                              uploaded ? "upload-box--has-file" : ""
                            } ${!uploaded && showDocError ? "upload-error" : ""}`}
                            onClick={() => {
                              if (!uploaded) handleUploadClick(key);
                            }}
                            onKeyDown={(ev) => {
                              if (
                                !uploaded &&
                                (ev.key === "Enter" || ev.key === " ")
                              ) {
                                ev.preventDefault();
                                handleUploadClick(key);
                              }
                            }}
                            role={uploaded ? undefined : "button"}
                            tabIndex={uploaded ? undefined : 0}
                          >
                            {uploaded && (
                              <div className="upload-slot__file-actions">
                                <CheckIcon />
                                <button
                                  type="button"
                                  className="upload-slot__remove-btn"
                                  onClick={(ev) =>
                                    handleRemoveDocument(key, ev)
                                  }
                                  aria-label={`Remove ${DOC_LABELS[key]}`}
                                >
                                  <TrashIcon />
                                </button>
                              </div>
                            )}

                            <input
                              type="file"
                              hidden
                              name={key}
                              accept={
                              key === "photograph"
                                ? ".jpg,.jpeg,.png"
                                : ".jpg,.jpeg,.png,.pdf"
                            }
                              onChange={(e) => handleFileUpload(key, e)}
                            />
                            {uploaded ? (
                              <div className="upload-preview-wrap">
                                <div className="upload-preview">
                                  <DocumentFilePreview file={uploaded} />
                                </div>
                              </div>
                            ) : (
                              <span className="upload-icon">
                                <svg
                                  width="15"
                                  height="15"
                                  viewBox="0 0 15 15"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M15 9.37549V14.3755C15 14.5413 14.9342 14.7002 14.8169 14.8174C14.6997 14.9346 14.5408 15.0005 14.375 15.0005H0.625C0.45924 15.0005 0.300269 14.9346 0.183058 14.8174C0.0658481 14.7002 0 14.5413 0 14.3755V9.37549C0 9.20973 0.0658481 9.05076 0.183058 8.93355C0.300269 8.81634 0.45924 8.75049 0.625 8.75049C0.79076 8.75049 0.949731 8.81634 1.06694 8.93355C1.18415 9.05076 1.25 9.20973 1.25 9.37549V13.7505H13.75V9.37549C13.75 9.20973 13.8158 9.05076 13.9331 8.93355C14.0503 8.81634 14.2092 8.75049 14.375 8.75049C14.5408 8.75049 14.6997 8.81634 14.8169 8.93355C14.9342 9.05076 15 9.20973 15 9.37549ZM4.81719 4.19268L6.875 2.13409V9.37549C6.875 9.54125 6.94085 9.70022 7.05806 9.81743C7.17527 9.93464 7.33424 10.0005 7.5 10.0005C7.66576 10.0005 7.82473 9.93464 7.94194 9.81743C8.05915 9.70022 8.125 9.54125 8.125 9.37549V2.13409L10.1828 4.19268C10.3001 4.30995 10.4591 4.37584 10.625 4.37584C10.7909 4.37584 10.9499 4.30995 11.0672 4.19268C11.1845 4.0754 11.2503 3.91634 11.2503 3.75049C11.2503 3.58464 11.1845 3.42558 11.0672 3.3083L7.94219 0.183304C7.88414 0.125194 7.81521 0.0790944 7.73934 0.0476417C7.66346 0.016189 7.58213 0 7.5 0C7.41787 0 7.33654 0.016189 7.26066 0.0476417C7.18479 0.0790944 7.11586 0.125194 7.05781 0.183304L3.93281 3.3083C3.81554 3.42558 3.74965 3.58464 3.74965 3.75049C3.74965 3.91634 3.81554 4.0754 3.93281 4.19268C4.05009 4.30995 4.20915 4.37584 4.375 4.37584C4.54085 4.37584 4.69991 4.30995 4.81719 4.19268Z"
                                    fill="#97262A"
                                  />
                                </svg>
                              </span>
                            )}
                          </div>
                        </div>
                        {documentSlotErrors[key] ? (
                          <p className="upload-doc-row__error" role="alert">
                            {documentSlotErrors[key]}
                          </p>
                        ) : null}
                      </div>
                    );
                  })}

                <p className="upload-note">{TEXT_CONTENT.UPLOAD_NOTE}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="proposal-form-actions">
          {pivcError && <p className="pivc-error">{pivcError}</p>}
          {showDocError && (
            <p className="pivc-error">{TEXT_CONTENT.PIVC_ERROR}</p>
          )}
          <button
            className={` proposal-btn-continue proposal-btn-continue btn ${isVerified ? "secondary-button" : "primary-button"}  regular`}
            onClick={handlePreIssuanceVerification}
            disabled={isVerified}
          >
            {isVerified ? "Pre-Issuance Verified" : "Pre-Issuance Verification"}
          </button>

          <button
            className={`proposal-btn-continue proposal-btn-continue btn ${isVerified ? "primary-button" : "secondary-button"} regular`}
            onClick={handleOtpValidation}
            disabled={loadingOtp}
          >
            {loadingOtp ? "Generating OTP..." : "Validate with OTP"}
          </button>
        </div>
      </div>
      {showOtpModal && (
        <div className="otp-modal-overlay">
          <div className="otp-modal">
            <div className="otp-header">
              <h5 className="otp-modal-title light">OTP Verification</h5>
            </div>
            <button
              className="otp-modal-close"
              onClick={() => setShowOtpModal(false)}
              aria-label="Close"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 15 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.781 13.7198C14.8507 13.7895 14.906 13.8722 14.9437 13.9632C14.9814 14.0543 15.0008 14.1519 15.0008 14.2504C15.0008 14.349 14.9814 14.4465 14.9437 14.5376C14.906 14.6286 14.8507 14.7114 14.781 14.781C14.7114 14.8507 14.6286 14.906 14.5376 14.9437C14.4465 14.9814 14.349 15.0008 14.2504 15.0008C14.1519 15.0008 14.0543 14.9814 13.9632 14.9437C13.8722 14.906 13.7895 14.8507 13.7198 14.781L7.50042 8.56073L1.28104 14.781C1.14031 14.9218 0.94944 15.0008 0.750417 15.0008C0.551394 15.0008 0.360523 14.9218 0.219792 14.781C0.0790615 14.6403 3.92322e-09 14.4494 0 14.2504C-3.92322e-09 14.0514 0.0790615 13.8605 0.219792 13.7198L6.4401 7.50042L0.219792 1.28104C0.0790615 1.14031 0 0.94944 0 0.750417C0 0.551394 0.0790615 0.360523 0.219792 0.219792C0.360523 0.0790615 0.551394 0 0.750417 0C0.94944 0 1.14031 0.0790615 1.28104 0.219792L7.50042 6.4401L13.7198 0.219792C13.8605 0.0790615 14.0514 -3.92322e-09 14.2504 0C14.4494 3.92322e-09 14.6403 0.0790615 14.781 0.219792C14.9218 0.360523 15.0008 0.551394 15.0008 0.750417C15.0008 0.94944 14.9218 1.14031 14.781 1.28104L8.56073 7.50042L14.781 13.7198Z"
                  fill="#3C3D3D"
                />
              </svg>
            </button>
            <div className="otp-body">
              <h6 className="large-body semibold">Enter OTP Code</h6>

              <p className="small-body regular">
                We've sent a 6-digit OTP to your mobile number
              </p>

              <div className="otp-phone">
                <p>
                  <span>Phone Number:</span>{" "}
                  <strong>{maskMobile(displayMobile)}</strong>
                </p>
              </div>

              <div className="form-field">
                <label className="label-one">Enter 6 digit OTP</label>
                <input
                  type="password"
                  maxLength={6}
                  placeholder="X X X X X X"
                  className="otp-input"
                  value={otpValue}
                  onChange={(e) => {
                    setOtpValue(e.target.value.replace(/\D/g, "").slice(0, 6));
                    if (otpError) {
                      setOtpError("");
                    }
                  }}
                />

                {otpError && <p className="pivc-error">{otpError}</p>}

                <span
                  className={`otp-resend ${resendDisabled ? "disabled" : ""}`}
                  onClick={handleResendOtp}
                >
                  {resendDisabled
                    ? `Resend OTP in ${resendTimer}s`
                    : "Resend OTP"}
                </span>
              </div>
              <div className="otp-footer">
                <button
                  className="otp-continue-btn btn primary-button regular"
                  disabled={completingJourney || otpValue.length != 6}
                  onClick={handleVerifyOtp}
                >
                  {completingJourney ? "Completing..." : "Continue"}
                </button>
                <p className="otp-secure">100% Secure. No Spam Guarantee</p>
              </div>
            </div>
          </div>
        </div>
      )}
      <NeedhelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title="Reach us through your preferred mode"
        type="success"
      />
    </>
  );
};

export default ProposalReview;
